import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { Goal, SubGoal, Todo } from '../services/goalService';
import GoalCard from '../components/GoalCard';
import FullscreenInspiration from '../components/FullscreenInspiration';
import Icon from '../components/Icon';
import Logo from '../components/Logo';
import BaseStats from '../components/BaseStats/BaseStats';
import EmptyState from '../components/EmptyState/EmptyState';
import { calculateGoalCompletionPercentage } from '../utils/goalProgress';
import { compareLastUpdated } from '../utils/goalOrder';
import { INACTIVITY_TIMEOUT } from '../constants';
import useInactivityTimer from '../hooks/useInactivityTimer';
import { useInspirationScreenPreference } from '../hooks/useInspirationScreenPreference';
import { useTranslation } from '../i18n';
import MotivationBanner from '../components/MotivationBanner/MotivationBanner';
import './HomePage.css';
import { formatPercent } from '../utils/format';
import { GoalCardsSkeleton } from '../components/Skeleton/Skeleton';
import { AiQuotaSummary } from '../components/AiQuota/AiQuota';
import BackgroundPlanProgress from '../components/AIGoalModal/BackgroundPlanProgress';
import HomeToday from '../components/HomeToday/HomeToday';
import HomeContinue, { continueItems, useTreeTimerSync } from '../components/HomeContinue/HomeContinue';
import { useTimers } from '../contexts/TimerContext';
import CompletedGoalsGroup, { splitCompleted } from '../components/CompletedGoalsGroup/CompletedGoalsGroup';

interface HomePageProps {
  goals: Goal[];
  loading?: boolean;
  onUpdateGoal: (id: string, data: Partial<Goal>) => void;
  onToggleFavorite: (id: string) => void;
  onAddSubGoal: (goalId: string, title: string, description: string) => void;
  onUpdateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => void;
  onDeleteSubGoal: (goalId: string, subGoalId: string) => void;
  onUpdateTodo?: (subGoalId: string, todoId: string, data: Partial<Todo>) => void;
  onNavigateToTasks?: () => void;
  onShowAIModal?: () => void;
  onShowForm?: () => void;
  onShowCreationModal?: () => void;
  /** Reloads the goal tree when a timer started or stopped anywhere leaves it behind. */
  onTimerChanged?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ 
  goals,
  loading = false,
  onUpdateGoal, 
  onToggleFavorite, 
  onAddSubGoal, 
  onUpdateSubGoal, 
  onDeleteSubGoal, 
  onUpdateTodo, 
  onNavigateToTasks,
  onShowAIModal,
  onShowForm,
  onShowCreationModal,
  onTimerChanged
}) => {
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const { t, language } = useTranslation();
  const deferredGoals = useDeferredValue(goals);
  const { enabled: inspirationEnabled } = useInspirationScreenPreference();
  const { running } = useTimers();
  const started = useMemo(() => continueItems(deferredGoals, running), [deferredGoals, running]);
  useTreeTimerSync(goals, running, onTimerChanged);
  const startedIds = useMemo(() => new Set(started.map((item) => item.todoId)), [started]);

  useInactivityTimer(() => {
    setIsFullscreenOpen(true);
  }, { timeout: INACTIVITY_TIMEOUT, enabled: inspirationEnabled });

  const summary = useMemo(() => {
    let completedGoals = 0;
    let totalTodos = 0;
    let completedTodos = 0;
    let progressSum = 0;
    const favoriteGoals: Goal[] = [];

    deferredGoals.forEach((goal) => {
      if (goal.completed) {
        completedGoals += 1;
      }

      if (goal.isFavorite) {
        favoriteGoals.push(goal);
      }

      progressSum += calculateGoalCompletionPercentage(goal);

      goal.subGoals.forEach((subGoal) => {
        const todos = subGoal.todos || [];
        totalTodos += todos.length;
        completedTodos += todos.filter((todo) => todo.completed).length;
      });
    });

    return {
      totalGoals: deferredGoals.length,
      completedGoals,
      overallProgress: deferredGoals.length > 0 ? Math.round(progressSum / deferredGoals.length) : 0,
      totalTodos,
      completedTodos,
      // The last updated first (T-118)
      favoriteGoals: favoriteGoals.sort(compareLastUpdated)
    };
  }, [deferredGoals]);

  const homeStats = useMemo(() => [
    { value: summary.totalGoals, label: t('home.stats.totalGoals'), icon: 'goal' as const },
    { value: summary.completedGoals, label: t('home.stats.completedGoals'), icon: 'circleCheck' as const },
    { value: formatPercent(summary.overallProgress, language), label: t('home.stats.overallProgress'), icon: 'chart' as const },
    { value: `${summary.completedTodos}/${summary.totalTodos}`, label: t('home.stats.completedTasks'), icon: 'note' as const }
  ], [summary, t, language]);

  const handleOpenGoal = useCallback((goalId: string) => {
    window.location.hash = `#/goals/${goalId}`;
  }, []);

  const handleGoToGoals = useCallback(() => {
    window.location.hash = '#/goals';
  }, []);

  const renderFavorite = useCallback((goal: Goal) => (
    <GoalCard
      key={goal.id}
      goal={goal}
      onUpdateGoal={onUpdateGoal}
      onToggleFavorite={onToggleFavorite}
      onAddSubGoal={onAddSubGoal}
      onUpdateSubGoal={onUpdateSubGoal}
      onDeleteSubGoal={onDeleteSubGoal}
      onUpdateTodo={onUpdateTodo}
      onNavigateToTasks={onNavigateToTasks}
      isListView={true}
      hideActions={true}
      onClick={() => handleOpenGoal(goal.id)}
    />
  ), [
    onUpdateGoal,
    onToggleFavorite,
    onAddSubGoal,
    onUpdateSubGoal,
    onDeleteSubGoal,
    onUpdateTodo,
    onNavigateToTasks,
    handleOpenGoal,
  ]);

  // Completed favorites fold under the open ones (T-127)
  const favorites = useMemo(() => splitCompleted(summary.favoriteGoals), [summary.favoriteGoals]);

  const hasNoGoals = deferredGoals.length === 0;

  return (
    <div className="tab-content">
      {loading && hasNoGoals ? (
        <GoalCardsSkeleton count={4} />
      ) : (
      <>
      {/* What you started, then what to do today, come first (T-087, T-153; same as native). */}
      <HomeContinue items={started} onChanged={onTimerChanged} />
      <HomeToday goals={deferredGoals} onUpdateTodo={onUpdateTodo} excludeIds={startedIds} />
      <MotivationBanner goals={goals} />
      <div className="welcome-section">
        {/* A new user sees the way to a first goal, not four zeros (T-087). */}
        {!hasNoGoals && <BaseStats stats={homeStats} />}
        <BackgroundPlanProgress onOpen={onShowAIModal} />
        <AiQuotaSummary />
      </div>

      {/* Boş Durum - Hedef yoksa */}
      {hasNoGoals && (
        <EmptyState
          classPrefix="home-empty"
          visual={
            <div className="home-empty-visual">
              <div className="visual-backdrop"></div>
              <div className="visual-icon-wrapper">
                <Logo size={80} className="visual-logo" />
              </div>
              <div className="visual-sparkle sparkle-1">✦</div>
              <div className="visual-sparkle sparkle-2">✦</div>
              <div className="visual-sparkle sparkle-3">✦</div>
            </div>
          }
          title={t('home.empty.title')}
          description={t('home.empty.description')}
          actions={
            <div className="home-empty-buttons">
              <button className="home-empty-cta cta-ai" onClick={onShowAIModal}>
                <Icon name="sparkles" className="cta-icon" />
                {t('home.empty.ctaAI')}
              </button>
              <button className="home-empty-cta cta-manual" onClick={onShowForm}>
                <Icon name="edit" className="cta-icon" />
                {t('home.empty.ctaManual')}
              </button>
            </div>
          }
        >
          <div className="home-empty-features">
            <div className="feature-item">
              <Icon name="goal" className="feature-icon" />
              <span>{t('home.empty.feature1')}</span>
            </div>
            <div className="feature-item">
              <Icon name="chart" className="feature-icon" />
              <span>{t('home.empty.feature2')}</span>
            </div>
            <div className="feature-item">
              <Icon name="circleCheck" className="feature-icon" />
              <span>{t('home.empty.feature3')}</span>
            </div>
          </div>
        </EmptyState>
      )}

      {/* Favori Hedefler Bölümü */}
      {summary.favoriteGoals.length > 0 && (
        <div className="favorite-goals-section">
          <div className="section-header">
            <h3>
              <Icon name="star" className="section-title-icon" />
              {t('home.favorites.title')}
            </h3>
            <button className="view-all-goals-btn" onClick={handleGoToGoals}>
              {t('home.favorites.viewAll')}
              <Icon name="chevronRight" />
            </button>
          </div>
          {favorites.active.length > 0 && (
            <div className="favorite-goals-grid">
              {favorites.active.map(renderFavorite)}
            </div>
          )}
          <CompletedGoalsGroup
            place="home"
            goals={favorites.completed}
            renderCard={renderFavorite}
            listClassName="favorite-goals-grid"
            alwaysOpen={favorites.active.length === 0}
          />
        </div>
      )}

      {/* Favori Yok - Bilgilendirme */}
      {!hasNoGoals && summary.favoriteGoals.length === 0 && (
        <div className="no-favorites-section">
          <div className="no-favorites-content">
            <div className="no-favorites-icon">
              <Icon name="star" className="star-icon" />
            </div>
            <h3 className="no-favorites-title">{t('home.noFavorites.title')}</h3>
            <p className="no-favorites-description">{t('home.noFavorites.description')}</p>
            <div className="no-favorites-tip">
              <Icon name="info" className="tip-icon" />
              <span>{t('home.noFavorites.tip')}</span>
            </div>
            <button 
              className="no-favorites-cta"
              onClick={handleGoToGoals}
            >
              <Icon name="goal" className="cta-icon" />
              {t('home.noFavorites.cta')}
            </button>
          </div>
        </div>
      )}

      {/* Floating Add Goal Button */}
      {!hasNoGoals && (
        <button
          className="fab-add-goal"
          onClick={onShowCreationModal || onShowForm}
          aria-label={t('goals.addGoal')}
          title={t('goals.addGoal')}
        >
          <Icon name="plus" />
        </button>
      )}
      </>
      )}

      {/* Fullscreen Inspiration Component */}
      <FullscreenInspiration
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
      />
    </div>
  );
};

export default React.memo(HomePage);
